import { useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import type React from 'react'
import { useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import * as yup from 'yup'
import DefaultModal from '../../../../../../components/DefaultModal'
import FormInput from '../../../../../../components/FormInput'
import Toast from '../../../../../../components/Toast'
import { useChangeUserEmailMutation } from '../../../../../../redux/services/coreApi'
import { UserDto } from '../../../../../../types/coreApi-types'

type ChangeUserEmailFormData = {
  newEmail: string
}

interface ChangeUserEmailModalProps {
  isOpen: boolean
  onClose: () => void
  user?: UserDto
}

const ChangeUserEmailModal: React.FC<ChangeUserEmailModalProps> = ({
  isOpen,
  onClose,
  user,
}) => {
  const toast = useToast()
  const [changeUserEmail, { isLoading }] = useChangeUserEmailMutation()

  const schema: yup.SchemaOf<ChangeUserEmailFormData> = yup.object({
    newEmail: yup
      .string()
      .email('Enter a valid email address')
      .required('This field is required'),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ChangeUserEmailFormData>({
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
  })

  useEffect(() => {
    if (isOpen) reset({ newEmail: '' })
  }, [isOpen, reset])

  const onSubmit: SubmitHandler<ChangeUserEmailFormData> = async ({
    newEmail,
  }) => {
    if (!user) return

    try {
      await changeUserEmail({ userId: user.id, newEmail }).unwrap()

      toast({
        position: 'bottom-right',
        render: () => <Toast type="success">Email address updated</Toast>,
      })
      onClose()
    } catch (err) {
      // Surface the server's reason verbatim — e.g. the address is already in use by another account, which
      // is exactly the duplicate-profile collision an operator hits when moving an email across accounts.
      let errMsg = (err as any)?.data?.errors
      if (!errMsg) errMsg = 'Something went wrong trying to change the email.'

      toast({
        position: 'bottom-right',
        render: () => <Toast type="error">{errMsg}</Toast>,
      })
    }
  }

  return (
    <DefaultModal
      isLoading={isLoading}
      isOpen={isOpen}
      onClose={onClose}
      title={'Change login email'}
      isLoadingText="Changing email..."
      whiteBG
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-5 pb-0 space-y-3">
          <div className="flex flex-col md:flex-row">
            <p className="text-description mb-0.5 md:mb-0 md:w-[180px]">
              Current email
            </p>
            <p className="text-default-small break-all">{user?.email || '—'}</p>
          </div>

          <FormInput
            label="New email"
            inputMode="email"
            registeredForm={register('newEmail')}
            error={errors?.newEmail}
          />

          <p className="text-description text-xs">
            This changes the customer's login identity. The new address is set as
            unverified and the customer is sent a verification email — they'll
            sign in with the new address going forward. An address already used by
            another account can't be taken until that account is freed.
          </p>
        </div>

        <div className="p-5 pt-0">
          <button type="submit" className="button mt-10">
            Change email
          </button>
        </div>
      </form>
    </DefaultModal>
  )
}

export default ChangeUserEmailModal
